import type { CatalogComponents } from '@lifeserver/xsolla';
import {
    LANGUAGE_TYPE_TO_XSOLLA_LOCALE,
    type XsollaTwoLetterLocale,
} from '@lifeserver/xsolla';

import Loading from '../../../shared/Loading';

import { useSelectedLanguage } from '../../../hooks/selectedLanugage';
import { useXsollaVirtualItem } from '../../../hooks/xsolla';

import { errorLikeToMessage } from '../../../utils/error';

import styles from './XsollaVirtualItem.module.scss';

type XsollaVirtualItemProps = {
    skus: string[];
};

type XsollaTwoLetterLocalizationObject = NonNullable<
    CatalogComponents['schemas']['two-letter-locale']
>;
type XsollaLocalizationObject =
    | CatalogComponents['schemas']['name-localization-object']
    | CatalogComponents['schemas']['description-localization-object'];

function getXsollaLocalizedValue(
    localization: XsollaLocalizationObject | undefined,
    xsollaLanguage: XsollaTwoLetterLocale,
) {
    if (!localization) {
        return undefined;
    }

    return (localization as XsollaTwoLetterLocalizationObject)[xsollaLanguage];
}

export function XsollaVirtualItem({ skus }: XsollaVirtualItemProps) {
    const sku = skus[0];
    const { selectedLanguage } = useSelectedLanguage();
    const { data: virtualItem, isLoading, error } = useXsollaVirtualItem(sku);

    if (skus.length !== 1) {
        return (
            <div>
                Expected exactly 1 Xsolla SKU, but got {skus.length} SKUs.
            </div>
        );
    }

    if (isLoading) {
        return <Loading style={{ textAlign: 'center' }} />;
    }

    if (error) {
        const errorMessage = errorLikeToMessage(error);
        return (
            <div className={styles.error}>
                Failed to load Xsolla virtual item: {errorMessage}
            </div>
        );
    }

    if (!virtualItem) {
        return <div>No virtual item found for SKU: {sku}</div>;
    }

    const xsollaLanguage =
        LANGUAGE_TYPE_TO_XSOLLA_LOCALE[selectedLanguage] ?? 'en';
    const name = getXsollaLocalizedValue(virtualItem.name, xsollaLanguage);
    const description = getXsollaLocalizedValue(
        virtualItem.description,
        xsollaLanguage,
    );

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                {virtualItem.image_url && (
                    <img
                        className={styles.image}
                        src={virtualItem.image_url}
                        alt={name || sku}
                    />
                )}
                <div className={styles.info}>
                    <div className={styles.row}>
                        <strong>SKU:</strong> {virtualItem.sku}
                    </div>
                    <div className={styles.row}>
                        <strong>Name:</strong> {name}
                    </div>
                    <div className={styles.row}>
                        <strong>Description:</strong> {description}
                    </div>
                </div>
            </div>

            {virtualItem.prices && virtualItem.prices.length > 0 && (
                <div className={styles.section}>
                    <h4>Prices</h4>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Currency</th>
                                <th>Amount</th>
                                <th>Default</th>
                            </tr>
                        </thead>
                        <tbody>
                            {virtualItem.prices.map((price) => (
                                <tr
                                    key={`${price.country_iso}-${price.currency}-${price.amount}`}
                                >
                                    <td>{price.currency}</td>
                                    <td>{price.amount}</td>
                                    <td>{price.is_default ? 'Yes' : 'No'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
